import { FeatureGates, useFeatureGate } from '@services/statsig';

interface EligibilityAccess {
  /** Whether the org has eligibility-checks access. False while loading. */
  hasAccess: boolean;
  /** Whether the underlying access data is still loading. */
  isLoading: boolean;
}

/**
 * Source of truth for eligibility-checks access in the web app.
 *
 * Sibling to `useJotBillAccess`. Eligibility is governed by the
 * `feature_eligibility_checks` Statsig gate — the same signal that
 * `useEligibilityBadgeState` and `EligibilityChecksPage` gate on.
 *
 * Eligibility checks require a default billing identity with an NPI, which is
 * captured on the Billing Identities surface in Claim Submission Setup. That
 * surface otherwise gates on `useJotBillAccess`, so an eligibility-only org
 * (eligibility gate on, no JotBill) has no self-serve path to configure the
 * prerequisite. Consumers OR this against `useJotBillAccess` to open just the
 * billing-identity capture — the full claims suite stays behind JotBill.
 */
export function useEligibilityAccess(): EligibilityAccess {
  const hasAccess = useFeatureGate(FeatureGates.ELIGIBILITY_CHECKS);

  // `useFeatureGate` resolves synchronously from the initialized Statsig
  // client, so there is no separate loading state to surface here.
  return { hasAccess, isLoading: false };
}
